import { get } from "svelte/store"
import { baseAPIURL } from "../../_shared/_logic/config"
import { apiBaseOverride } from "../../../_shared/_logic/store"
import { buildQueryString, request, requestBlobWithHeaders } from "../client"
import type { ContactOrder } from "../contact-orders"
import type { CostBenefitEstimate } from "../cost-benefit"

export type LetterSettingsPayload = {
  portal?: LetterPortalCopy | null
  personal_letter_sender?: PersonalLetterSenderSettings | null
  letter_signature?: OrganizationLetterSignature | null
  letter_address_window?: LetterAddressWindow | null
  personal_letter_title_text?: string | null
  personal_letter_subtitle_text?: string | null
  personal_letter_intro_text?: string | null
  personal_letter_portal_heading_text?: string | null
  personal_letter_reschedule_title_text?: string | null
  personal_letter_reschedule_subtitle_text?: string | null
  personal_letter_reschedule_intro_text?: string | null
  personal_letter_reschedule_portal_heading_text?: string | null
  klebezettel_title_text?: string | null
  klebezettel_subtitle_text?: string | null
  klebezettel_intro_text?: string | null
  klebezettel_qr_heading_text?: string | null
  klebezettel_qr_body_text?: string | null
}

export type LetterAddressWindow = {
  sender_left_mm: number
  sender_top_mm: number
  sender_width_mm: number
  sender_height_mm: number
  recipient_left_mm: number
  recipient_top_mm: number
  recipient_width_mm: number
  recipient_height_mm: number
}

export type LetterPortalStep = { enabled: boolean; title: string; body: string }
export type LetterPortalCopy = {
  open_portal: LetterPortalStep
  confirm_appointment: LetterPortalStep
  choose_alternative: LetterPortalStep
  complete: LetterPortalStep
  response_hint: string
  support_heading: string
  support_body: string
}
export type LetterTemplateVariant = "announcement" | "reschedule" | "aushang"
export type LetterPreviewMode = "personal" | "impersonal"
export type LetterPreviewCheck = {
  variant: LetterTemplateVariant
  recipient_mode: LetterPreviewMode
  page_count: number
}
export type LetterTemplatePreview = {
  pdf_base64: string
  page_count: number
  checks: LetterPreviewCheck[]
}

export const previewLetterTemplate = (
  organizationId: string,
  settings: LetterSettingsPayload,
  variant: LetterTemplateVariant,
  recipientMode: LetterPreviewMode,
) =>
  request<LetterTemplatePreview>(`/letters/settings/${organizationId}/preview`, {
    method: "POST",
    body: JSON.stringify({ settings, variant, recipient_mode: recipientMode }),
  })

export const updateChannelTemplates = (
  organizationId: string,
  channel: "email" | "sms" | "phone",
  templates: Record<string, Record<string, string>>,
) =>
  request<OrganizationContactPolicy>(`/contact-policy/${organizationId}/templates/${channel}`, {
    method: "PATCH",
    body: JSON.stringify({ templates }),
  })

export type WorkOrderLetterAddress = {
  label: string
  lines: string[]
  street: string | null
  house_number: string | null
  zip: string | null
  city: string | null
  country: string | null
  address_extra: string | null
}

export type WorkOrderLetterRecipientContactAddress = {
  contact_point_id: string
  label: string | null
  address: WorkOrderLetterAddress
}

export type WorkOrderLetterRecipientCustomer = {
  customer_id: string
  display_name: string
  contact_addresses: WorkOrderLetterRecipientContactAddress[]
}

export type WorkOrderLetterRecipientSource =
  | "OBJECT_ADDRESS"
  | "CUSTOMER_CONTACT_ADDRESS"
  | "IMPERSONAL"
export type WorkOrderLetterMode = "personal" | "impersonal" | "generic"

export type WorkOrderLetterTarget = {
  source: WorkOrderLetterRecipientSource | string
  recipient_name: string | null
  recipient_customer_id: string | null
  recipient_contact_point_id: string | null
  address: WorkOrderLetterAddress
}

export type WorkOrderLetter = {
  work_order_id: string
  organization_id: string
  organization_name: string
  organization_slug: string
  order_number: string | null
  public_code: string
  public_code_source: "case" | "contact_step" | string
  property_address: string
  qr_url: string
  download_url: string
  message: string
  generated_at: string
  letter_mode?: WorkOrderLetterMode | string
  resolved_step_id?: string | null
  recipient_resolution_source?: string | null
  recipient_resolution_label?: string | null
  recipient_target: WorkOrderLetterTarget
  recipient_customers: WorkOrderLetterRecipientCustomer[]
  object_address: WorkOrderLetterAddress
}

export type WorkOrderLetterEditorProjection = {
  letter: WorkOrderLetter
  contact_order?: ContactOrder | null
  letter_step_id?: string | null
}

export type WorkOrderPrintMode = "PERSONAL_LETTER" | "IMPERSONAL_LETTER" | "GENERIC_KLEBEZETTEL"

export type WorkOrderPrintReference = Record<string, unknown>

export type WorkOrderPrintRecipientOption = {
  customer_id: string
  contact_point_id: string
  label: string
  sub_label?: string | null
  role?: string | null
  role_label?: string | null
  address_label?: string | null
  postal_address?: WorkOrderLetterAddress | null
}

export type WorkOrderPrintModeOption = {
  mode: WorkOrderPrintMode
  label: string
  disabled_reason?: string | null
}

export type WorkOrderPrintMarkableStep = {
  case_id: string
  contact_order_id: string
  contact_order_step_id: string
  label: string
  status?: string | null
}

export type DueLetterDeliveryStep = {
  contact_order_id: string
  contact_order_step_id: string
  due_at: string | null
  letter_mode?: string | null
  print_ready_at?: string | null
}

export type WorkOrderPrintPreviewRow = {
  work_order_id: string
  work_order_reference?: WorkOrderPrintReference | null
  print_group_id?: string | null
  property_id?: string | null
  property_unit_id?: string | null
  unit_label?: string | null
  property_unit_type?: string | null
  has_residential_units?: boolean | null
  is_multi_unit_property?: boolean | null
  property_unit_count?: number | null
  supports_generic_klebezettel?: boolean | null
  order_number?: string | null
  order_type_internal_label?: string | null
  work_order_label?: string | null
  work_order_sub_label?: string | null
  property_label: string
  property_address?: WorkOrderLetterAddress | null
  appointment_start_at?: string | null
  appointment_end_at?: string | null
  appointment_label?: string | null
  announcement_label?: string | null
  default_mode: WorkOrderPrintMode
  available_modes: WorkOrderPrintModeOption[]
  recipient_options: WorkOrderPrintRecipientOption[]
  selected_recipient_customer_id?: string | null
  selected_recipient_contact_point_id?: string | null
  markable_step?: WorkOrderPrintMarkableStep | null
  search_text?: string | null
  disabled_reason?: string | null
}

export type WorkOrderPrintPreviewRequest = {
  organization_id: string
  work_order_ids: string[]
}

export type WorkOrderPrintPreviewResponse = {
  requested_count: number
  rows: WorkOrderPrintPreviewRow[]
}

export type WorkOrderPrintRowSelection = {
  delivery_item_id?: string | null
  work_order_id: string
  work_order_reference?: WorkOrderPrintReference | null
  mode: WorkOrderPrintMode
  work_order_label?: string | null
  order_number?: string | null
  order_type_internal_label?: string | null
  work_order_sub_label?: string | null
  property_id?: string | null
  property_unit_id?: string | null
  property_label?: string | null
  property_address_label?: string | null
  property_address_lines?: string[] | null
  unit_label?: string | null
  appointment_start_at?: string | null
  appointment_end_at?: string | null
  appointment_label?: string | null
  recipient_customer_id?: string | null
  recipient_contact_point_id?: string | null
  recipient_label?: string | null
  recipient_name?: string | null
  recipient_address?: WorkOrderLetterAddress | null
  property_address?: WorkOrderLetterAddress | null
  case_id?: string | null
  contact_order_id?: string | null
  contact_order_step_id?: string | null
  letter_delivery_batch_item_id?: string | null
  action_case_id?: string | null
}

const textForPrintRequest = (value: unknown): string => (value == null ? "" : String(value))

const nullableTextForPrintRequest = (value: unknown): string | null =>
  value == null ? null : String(value)

const stringListForPrintRequest = (value: unknown): string[] =>
  Array.isArray(value) ? value.flatMap((entry) => (entry == null ? [] : [String(entry)])) : []

export const normalizeWorkOrderLetterAddressForRequest = (
  address: WorkOrderLetterAddress | null | undefined,
): WorkOrderLetterAddress | null => {
  if (!address || typeof address !== "object") return null

  const source = address as Record<string, unknown>

  return {
    label: textForPrintRequest(source.label),
    lines: stringListForPrintRequest(source.lines),
    street: nullableTextForPrintRequest(source.street),
    house_number: nullableTextForPrintRequest(source.house_number),
    zip: nullableTextForPrintRequest(source.zip),
    city: nullableTextForPrintRequest(source.city),
    country: nullableTextForPrintRequest(source.country),
    address_extra: nullableTextForPrintRequest(source.address_extra),
  }
}

export const normalizeWorkOrderPrintRowForRequest = (
  row: WorkOrderPrintRowSelection,
): WorkOrderPrintRowSelection => ({
  ...row,
  recipient_address: normalizeWorkOrderLetterAddressForRequest(row.recipient_address),
  property_address: normalizeWorkOrderLetterAddressForRequest(row.property_address),
})

export const normalizeWorkOrderPrintRowsForRequest = (
  rows: WorkOrderPrintRowSelection[],
): WorkOrderPrintRowSelection[] => rows.map(normalizeWorkOrderPrintRowForRequest)

export type WorkOrderPrintPrepareRecipientsRequest = {
  organization_id: string
  rows: WorkOrderPrintRowSelection[]
}

export type WorkOrderPrintPrepareRecipientsResponse = {
  rows: WorkOrderPrintRowSelection[]
  updated_customers_count: number
  created_customers_count: number
  updated_contact_points_count: number
  created_contact_points_count: number
  updated_properties_count: number
}

export type WorkOrderPrintPageManifestItem = {
  first_page?: number | null
  page_count?: number | null
  print_page_id: string
  row_index: number
  work_order_id: string
  mode: WorkOrderPrintMode
  contact_order_step_id?: string | null
  letter_delivery_batch_item_id?: string | null
  action_case_id?: string | null
  property_id?: string | null
  property_unit_id?: string | null
  property_label?: string | null
  unit_label?: string | null
  delivery_group_key?: string | null
  delivery_group_label?: string | null
  automatic_delivery_eligible: boolean
  automatic_delivery_disabled_reason?: string | null
  order_label?: string | null
  recipient_label?: string | null
  recipient_name?: string | null
  address_label?: string | null
  recipient_address?: WorkOrderLetterAddress | null
  property_address?: WorkOrderLetterAddress | null
}

export type WorkOrderPrintRenderManifest = {
  pages: WorkOrderPrintPageManifestItem[]
}

export type WorkOrderPrintFinalizeRequest = {
  organization_id: string
  rows: WorkOrderPrintRowSelection[]
}

export type WorkOrderPrintFinalizeRowResult = {
  work_order_id: string
  contact_order_step_id?: string | null
  status: string
  message: string
}

export type WorkOrderPrintFinalizeResponse = {
  updated_count: number
  skipped_count: number
  rows: WorkOrderPrintFinalizeRowResult[]
}

export type WorkOrderPrintJobStatusValue = "queued" | "running" | "finished" | "failed" | "expired"

export type WorkOrderPrintJobStatus = {
  job_id: string
  organization_id: string
  status: WorkOrderPrintJobStatusValue
  phase: string
  total_rows: number
  processed_rows: number
  message?: string | null
  filename?: string | null
  download_url?: string | null
  download_enabled?: boolean | null
  render_manifest?: WorkOrderPrintRenderManifest | null
  error_message?: string | null
  created_at: string
  updated_at: string
  expires_at?: string | null
}

export type WorkOrderPrintDeliveryEstimate = {
  automatic_delivery_provider_enabled: boolean
  automatic_delivery_provider_mode?: "test" | "live" | string | null
  automatic_delivery_provider_notice?: string | null
  automatic_delivery_provider_disabled_reason?: string | null
  automatic_count: number
  credit_cost_per_letter: number
  total_credits: number
  estimated_route_km: number
  estimated_drive_minutes: number
  estimated_handling_minutes: number
  estimated_saved_minutes: number
  missing_coordinate_count: number
  cost_benefit?: CostBenefitEstimate | null
}
const enforceHttpsWhenPageSecure = (url: string): string => {
  if (typeof window === "undefined") return url

  if (window.location.protocol !== "https:") return url

  try {
    const parsed = new URL(url, window.location.origin)

    if (parsed.protocol === "http:") {
      parsed.protocol = "https:"

      return parsed.toString()
    }

    return url
  } catch (err) {
    console.warn("Failed to enforce https for letter URL", err)

    return url
  }
}

const resolveApiBase = (): string => {
  const override = get(apiBaseOverride)
  const rawBase = (override && override.trim()) || baseAPIURL
  const normalized = rawBase.replace(/\/+$/, "")

  return normalized.endsWith("/api/v1") ? normalized : `${normalized}/api/v1`
}

export const fetchLetterSettings = (organizationId: string) =>
  request<LetterSettingsPayload>(`/letters/settings/${organizationId}`)

export const updateLetterSettings = (organizationId: string, payload: LetterSettingsPayload) =>
  request<LetterSettingsPayload>(`/letters/settings/${organizationId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  })

export type GenericKlebezettelPdfUrlOptions = {
  copies?: number | null
}

export const buildGenericKlebezettelPdfUrl = (
  organizationId: string,
  opts?: GenericKlebezettelPdfUrlOptions,
) =>
  enforceHttpsWhenPageSecure(
    `${resolveApiBase()}/admin/letters/organizations/${encodeURIComponent(organizationId)}/klebezettel/pdf${buildQueryString(
      {
        copies: opts?.copies && opts.copies > 1 ? opts.copies : undefined,
      },
    )}`,
  )

export type WorkOrderLettersBatchPdfUrlOptions = {
  mode?: Exclude<WorkOrderLetterMode, "generic"> | null
}

export const buildWorkOrderLettersBatchPdfUrl = (
  workOrderIds: string[],
  opts?: WorkOrderLettersBatchPdfUrlOptions,
) =>
  enforceHttpsWhenPageSecure(
    `${resolveApiBase()}/admin/letters/work-orders/batch/pdf${buildQueryString({
      work_order_ids: workOrderIds.join(","),
      mode: opts?.mode && opts.mode !== "personal" ? opts.mode : undefined,
    })}`,
  )

export const previewWorkOrderPrintBatch = (payload: WorkOrderPrintPreviewRequest) =>
  request<WorkOrderPrintPreviewResponse>("/letters/work-orders/print-preview", {
    method: "POST",
    body: JSON.stringify(payload),
  })

export const prepareWorkOrderPrintRecipients = (payload: WorkOrderPrintPrepareRecipientsRequest) =>
  request<WorkOrderPrintPrepareRecipientsResponse>("/letters/work-orders/print-recipients", {
    method: "POST",
    body: JSON.stringify({
      ...payload,
      rows: normalizeWorkOrderPrintRowsForRequest(payload.rows),
    }),
  })

export const startManualWorkOrderPrintDeliveryJob = (payload: {
  organization_id: string
  rows?: WorkOrderPrintRowSelection[]
  manual_delivery_item_ids: string[]
}) =>
  request<WorkOrderPrintJobStatus>("/letters/work-orders/print-jobs/manual-delivery", {
    method: "POST",
    body: JSON.stringify({
      ...payload,
      rows: normalizeWorkOrderPrintRowsForRequest(payload.rows ?? []),
    }),
  })

export const fetchWorkOrderPrintBatchJob = (jobId: string, organizationId: string) =>
  request<WorkOrderPrintJobStatus>(
    `/letters/work-orders/print-jobs/${jobId}${buildQueryString({
      organization_id: organizationId,
    })}`,
  )

export const estimateWorkOrderPrintDelivery = (
  organizationId: string,
  letterDeliveryBatchItemIds: string[],
  automaticDeliveryItemIds: string[],
) =>
  request<WorkOrderPrintDeliveryEstimate>(
    `/letters/work-orders/delivery-estimate${buildQueryString({
      organization_id: organizationId,
    })}`,
    {
      method: "POST",
      body: JSON.stringify({
        letter_delivery_batch_item_ids: letterDeliveryBatchItemIds,
        automatic_delivery_item_ids: automaticDeliveryItemIds,
      }),
    },
  )

export const buildManualWorkOrderPrintJobDeliveryDownloadUrl = (
  jobId: string,
  organizationId: string,
) =>
  enforceHttpsWhenPageSecure(
    `${resolveApiBase()}/admin/letters/work-orders/print-jobs/${encodeURIComponent(
      jobId,
    )}/manual-delivery-download${buildQueryString({ organization_id: organizationId })}`,
  )

/** Complete the contained manual letters and receive the PDF in the same request. */
export const downloadAndCompleteManualLetters = async (jobId: string, organizationId: string) => {
  const response = await requestBlobWithHeaders(
    `/letters/work-orders/print-jobs/${encodeURIComponent(jobId)}/manual-delivery-download${buildQueryString({ organization_id: organizationId })}`,
    { method: "POST", headers: { Accept: "application/pdf" } },
  )

  return { blob: response.data, contentDisposition: response.headers.get("content-disposition") }
}

export const buildWorkOrderPrintBatchJobDownloadUrl = (jobId: string, organizationId: string) =>
  enforceHttpsWhenPageSecure(
    `${resolveApiBase()}/admin/letters/work-orders/print-jobs/${encodeURIComponent(
      jobId,
    )}/download${buildQueryString({ organization_id: organizationId })}`,
  )

export type FetchWorkOrderLetterOptions = {
  stepId?: string | null
  mode?: WorkOrderLetterMode | string | null
  recipientSource?: WorkOrderLetterRecipientSource | string | null
  recipientCustomerId?: string | null
  recipientContactPointId?: string | null
}

export const fetchWorkOrderLetter = (workOrderId: string, opts?: FetchWorkOrderLetterOptions) =>
  request<WorkOrderLetter>(
    `/letters/work-orders/${workOrderId}${buildQueryString({
      step_id: opts?.stepId,
      mode: opts?.mode,
      recipient_source: opts?.recipientSource,
      recipient_customer_id: opts?.recipientCustomerId,
      recipient_contact_point_id: opts?.recipientContactPointId,
    })}`,
  ).then((letter) => ({
    ...letter,
    download_url: enforceHttpsWhenPageSecure(letter.download_url),
    qr_url: enforceHttpsWhenPageSecure(letter.qr_url),
  }))

export const fetchWorkOrderLetterEditorProjection = (
  workOrderId: string,
  opts?: FetchWorkOrderLetterOptions,
) =>
  request<WorkOrderLetterEditorProjection>(
    `/letters/work-orders/${workOrderId}${buildQueryString({
      projection: "editor",
      step_id: opts?.stepId,
      mode: opts?.mode,
      recipient_source: opts?.recipientSource,
      recipient_customer_id: opts?.recipientCustomerId,
      recipient_contact_point_id: opts?.recipientContactPointId,
    })}`,
  ).then((result) => ({
    ...result,
    letter: {
      ...result.letter,
      download_url: enforceHttpsWhenPageSecure(result.letter.download_url),
      qr_url: enforceHttpsWhenPageSecure(result.letter.qr_url),
    },
  }))
